import { toast } from 'sonner'
import { Card, CardContent } from '@/components/ui/card'
import { POInlineEditableField } from '@/components/po-inline-edit'
import { cn } from '@/lib/utils'
import { AutomationSwitch } from '@/components/automation-switch'
import {
  usePurchaseOrderAutomationHistory,
  usePurchaseOrderFreightBookingHistory,
  useUpdatePurchaseOrder,
} from '@/features/purchase-orders/hooks'
import { getApiErrorMessage } from '@/lib/api-error'

export function POInstructionsPanel({
  poUuid,
  value: upstreamValue,
  kind,
  embedded = false,
}: {
  poUuid: string
  value: string | null
  kind: 'po' | 'shipping'
  /** Render as a section inside the PO details card instead of as its own card. */
  embedded?: boolean
}) {
  const updatePO = useUpdatePurchaseOrder(poUuid)
  const title = kind === 'po' ? 'PO Instructions' : 'Shipping Instructions'
  const field = kind === 'po' ? 'instructions' : 'shipping_instructions'
  // Show what was typed while the save regenerates the PDF, instead of flashing the old text.
  const saving = updatePO.isPending ? updatePO.variables?.[field] : undefined
  const shown = saving ?? upstreamValue ?? ''

  const save = (next: string) => {
    updatePO.mutate(
      { [field]: next.trim() },
      {
        onSuccess: () => toast.success(`${title} saved — PO regenerated`),
        onError: (err) => toast.error(`Failed to save ${title.toLowerCase()}: ${getApiErrorMessage(err)}`),
      },
    )
  }

  const content = (
    <>
      {embedded ? (
        <h4 className="text-xs text-muted-foreground">{title}</h4>
      ) : (
        <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
      )}
      <POInlineEditableField
        kind="textarea"
        value={shown}
        placeholder="None"
        ariaLabel={`Edit ${title}`}
        title={`Edit ${title}`}
        pending={updatePO.isPending}
        allowWrap
        showEditIcon
        buttonClassName={cn('-mx-2 w-[calc(100%+1rem)] justify-between', !shown && 'italic text-muted-foreground')}
        onConfirm={save}
      />
    </>
  )

  if (embedded) {
    return (
      <section aria-label={title} className="space-y-1">
        {content}
      </section>
    )
  }

  return (
    <Card className="gap-0 py-0">
      <CardContent className="space-y-2 p-4">{content}</CardContent>
    </Card>
  )
}

export function POSettingsPanel({
  poUuid,
  automationDisabled,
  projectTrackingEnabled,
  weBookFreight,
  freightAutoBookingDisabled,
}: {
  poUuid: string
  automationDisabled: boolean
  projectTrackingEnabled: boolean
  weBookFreight: boolean
  freightAutoBookingDisabled: boolean
}) {
  const updatePO = useUpdatePurchaseOrder(poUuid)
  const history = usePurchaseOrderAutomationHistory(poUuid)
  const freightHistory = usePurchaseOrderFreightBookingHistory(poUuid)
  const projectBlocked = !projectTrackingEnabled
  const automationOn = !automationDisabled && projectTrackingEnabled
  const freightBookingOn = automationOn && !freightAutoBookingDisabled

  const handleFreightToggle = (checked: boolean) => {
    updatePO.mutate(
      { freight_auto_booking_disabled: !checked },
      {
        onError: (err) => toast.error(`Failed to update freight booking: ${getApiErrorMessage(err)}`),
      },
    )
  }

  const handleToggle = (checked: boolean) => {
    updatePO.mutate(
      { automation_disabled: !checked },
      {
        onError: (err) => toast.error(`Failed to update automation: ${getApiErrorMessage(err)}`),
      },
    )
  }

  return (
    <Card className="gap-0 py-0">
      <CardContent className="space-y-3 p-4">
        <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Settings
        </h3>
        <AutomationSwitch
          id="po-automation-toggle"
          description="PO emails, AI analysis, follow-ups, shipping, and fulfillment for this PO."
          note={
            projectBlocked
              ? 'Disabled at the project level. Re-enable there to turn automation back on.'
              : undefined
          }
          checked={automationOn}
          onCheckedChange={handleToggle}
          disabled={updatePO.isPending || projectBlocked}
          history={history.data}
          historyLoading={history.isLoading}
          showScope
        />
        {weBookFreight && (
          <div className="space-y-3 border-t pt-3">
            <AutomationSwitch
              id="po-freight-booking-toggle"
              label="Book freight automatically"
              description="Cheapest carrier once the supplier is ready. Off: staff choose the carrier."
              note={automationOn ? undefined : 'Off while automation is off.'}
              checked={freightBookingOn}
              onCheckedChange={handleFreightToggle}
              disabled={updatePO.isPending || !automationOn}
              history={freightHistory.data}
              historyLoading={freightHistory.isLoading}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}
